import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { formatXof, PAYMENT_METHODS } from '../utils/format';

const FEE_CATEGORIES = [
  'Frais de scolarité',
  'Frais de cantine/goûter',
  'Frais de tenue scolaire',
  'Frais de transport',
  "Frais de garde d'enfant",
  "Frais d'excursion",
  "Frais d'assurance scolaire",
  "Frais d'informatique",
  'Frais de bibliothèque',
  'Frais de bus scolaire',
  "Frais d'internat",
];

const OPERATION_TYPES = [
  'Paramétrage frais', 'Dépense', 'Frais divers', 'Paie',
  'Bulletin de paie', 'Avance sur salaire', 'Retenue sur salaire',
];

export default function Finance1() {
  const [tab, setTab] = useState('frais');

  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'frais' ? 'active' : ''}`} onClick={() => setTab('frais')}>Frais &amp; facturation</button>
        <button className={`tab ${tab === 'depenses' ? 'active' : ''}`} onClick={() => setTab('depenses')}>Dépenses &amp; paie</button>
      </div>
      {tab === 'frais' && <Frais />}
      {tab === 'depenses' && <Depenses />}
    </>
  );
}

// ── Frais & facturation ───────────────────────────────────
function Frais() {
  const [fees, setFees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classFees, setClassFees] = useState({});
  const [classAmounts, setClassAmounts] = useState({});
  const [error, setError] = useState('');
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [savedMatrix, setSavedMatrix] = useState(false);
  const [form, setForm] = useState({ name: '', amountXof: '', category: '' });
  const [editFee, setEditFee] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amountXof: '', category: '' });

  function loadFees() {
    api.get('/finance/fee-types').then(setFees).catch((e) => setError(e.message));
  }

  function loadClassFees() {
    api.get('/finance/class-fees').then((data) => {
      const map = {};
      const amts = {};
      (data || []).forEach(({ classId, feeTypeId, amountXof }) => {
        if (!map[classId]) map[classId] = new Set();
        map[classId].add(feeTypeId);
        if (amountXof != null) amts[`${classId}_${feeTypeId}`] = amountXof;
      });
      setClassFees(map);
      setClassAmounts(amts);
    }).catch(() => {});
  }

  useEffect(() => {
    loadFees();
    api.get('/catalog/classes').then(setClasses).catch(() => {});
    loadClassFees();
  }, []);

  function toggleFee(classId, feeTypeId) {
    setSavedMatrix(false);
    setClassFees((prev) => {
      const next = { ...prev };
      const s = new Set(next[classId] || []);
      if (s.has(feeTypeId)) s.delete(feeTypeId); else s.add(feeTypeId);
      next[classId] = s;
      return next;
    });
  }

  function updateClassAmount(classId, feeTypeId, value) {
    setSavedMatrix(false);
    setClassAmounts((prev) => ({ ...prev, [`${classId}_${feeTypeId}`]: value }));
  }

  async function saveClassFees() {
    setSavingMatrix(true); setError('');
    try {
      const assignments = [];
      Object.entries(classFees).forEach(([classId, feeIds]) => {
        feeIds.forEach((feeTypeId) => {
          const raw = classAmounts[`${classId}_${feeTypeId}`];
          const amt = raw !== undefined && raw !== '' ? Number(raw) : null;
          assignments.push({ classId, feeTypeId, amountXof: amt });
        });
      });
      await api.put('/finance/class-fees', { assignments });
      setSavedMatrix(true);
    } catch (e) { setError(e.message); }
    setSavingMatrix(false);
  }

  async function addFee(e) {
    e.preventDefault(); setError('');
    try {
      await api.post('/finance/fee-types', { name: form.name, amountXof: Number(form.amountXof), category: form.category || undefined });
      setForm({ name: '', amountXof: '', category: '' });
      loadFees();
    } catch (err) { setError(err.message); }
  }

  async function updateFee(e) {
    e.preventDefault(); setError('');
    try {
      await api.put(`/finance/fee-types/${editFee.id}`, { name: editForm.name, amountXof: Number(editForm.amountXof), category: editForm.category || undefined });
      setEditFee(null);
      loadFees();
    } catch (err) { setError(err.message); }
  }

  async function deleteFee(id) {
    if (!confirm('Supprimer ce type de frais ?')) return;
    setError('');
    try {
      await api.delete(`/finance/fee-types/${id}`);
      loadFees();
    } catch (err) { setError(err.message); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <div className="form-error">{error}</div>}

      {/* Types de frais */}
      <div className="card">
        <div className="card-head"><h2>Types de frais</h2></div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Libellé</th><th>Catégorie</th><th className="num">Montant par défaut</th><th>Actions</th></tr></thead>
            <tbody>
              {fees.length === 0
                ? <tr><td colSpan="4" className="empty">Aucun frais défini.</td></tr>
                : fees.map((f) => (
                    <tr key={f.id}>
                      <td>{f.name}</td>
                      <td>{f.category || '—'}</td>
                      <td className="num">{formatXof(f.amountXof)}</td>
                      <td>
                        <button className="btn btn-sm" onClick={() => { setEditFee(f); setEditForm({ name: f.name, amountXof: f.amountXof, category: f.category || '' }); }} style={{ marginRight: 6 }}>✏️</button>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteFee(f.id)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table></div>

          <div>
            <h3 style={{ marginBottom: 12, fontSize: '0.9rem', fontWeight: 600 }}>Ajouter un type de frais</h3>
            <form onSubmit={addFee} className="form-grid">
              <div className="field"><label>Libellé *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Frais d'inscription" required />
              </div>
              <div className="field"><label>Montant par défaut (XOF) *</label>
                <input type="number" min="0" value={form.amountXof} onChange={(e) => setForm({ ...form, amountXof: e.target.value })} required />
              </div>
              <div className="field"><label>Catégorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">—</option>
                  {FEE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <button className="btn" type="submit" style={{ width: 'auto', padding: '0 20px' }}>Ajouter</button>
            </form>
          </div>
        </div>
      </div>

      {/* Frais par classe */}
      <div className="card">
        <div className="card-head">
          <h2>Frais applicables par classe</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {savedMatrix && <span style={{ fontSize: '0.82rem', color: 'var(--success, green)' }}>✓ Enregistré</span>}
            <button className="btn btn-sm" onClick={saveClassFees} disabled={savingMatrix}>
              {savingMatrix ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
        <div className="card-body">
          {classes.length === 0 || fees.length === 0 ? (
            <div className="empty">Aucune classe ou aucun type de frais défini.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 120 }}>Classe</th>
                    {fees.map((f) => (
                      <th key={f.id} style={{ fontSize: 11, textAlign: 'center', maxWidth: 110, whiteSpace: 'normal', lineHeight: 1.3 }}>
                        {f.name}
                        <div style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 10 }}>défaut : {formatXof(f.amountXof)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {classes.map((cls) => (
                    <tr key={cls.id}>
                      <td><b>{cls.name}</b></td>
                      {fees.map((f) => {
                        const checked = !!(classFees[cls.id]?.has(f.id));
                        const amtKey = `${cls.id}_${f.id}`;
                        const amt = classAmounts[amtKey];
                        return (
                          <td key={f.id} style={{ textAlign: 'center', verticalAlign: 'top', padding: '8px 6px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleFee(cls.id, f.id)}
                                style={{ width: 20, height: 20, cursor: 'pointer' }}
                              />
                              {checked && (
                                <input
                                  type="number"
                                  min="0"
                                  value={amt ?? f.amountXof ?? ''}
                                  onChange={(e) => updateClassAmount(cls.id, f.id, e.target.value)}
                                  style={{ width: 88, fontSize: 11, textAlign: 'right', padding: '3px 6px', border: '1px solid var(--line)', borderRadius: 4, background: 'var(--surface)' }}
                                  placeholder={String(f.amountXof || 0)}
                                />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal édition */}
      {editFee && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h3>Modifier le type de frais</h3><button onClick={() => setEditFee(null)}>✕</button></div>
            <div className="modal-body">
              <form onSubmit={updateFee} className="form-grid">
                <div className="field"><label>Libellé *</label>
                  <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="field"><label>Montant par défaut (XOF) *</label>
                    <input type="number" min="0" value={editForm.amountXof} onChange={(e) => setEditForm({ ...editForm, amountXof: e.target.value })} required />
                  </div>
                  <div className="field"><label>Catégorie</label>
                    <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}>
                      <option value="">—</option>
                      {FEE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn" type="submit">Enregistrer</button>
                  <button className="btn btn-secondary" type="button" onClick={() => setEditFee(null)}>Annuler</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dépenses & paie ───────────────────────────────────────
function Depenses() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [staff, setStaff] = useState([]);

  function load() {
    api.get('/finance/payments?type=DEPENSE').then(setRows).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/staff').then(setStaff).catch(() => {});
  }, []);

  const totalDepenses = rows.reduce((s, r) => s + (r.amountXof || 0), 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Dépenses &amp; paie du personnel</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="badge navy">{formatXof(totalDepenses)} total</span>
          <button className="btn btn-sm" onClick={() => setModal({})}>+ Nouvelle dépense</button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucune dépense enregistrée.</div> : (
          <div className="table-wrap"><table>
            <thead>
              <tr><th>Type</th><th>Agent</th><th>Catégorie</th><th>Date</th><th>Moyen</th><th className="num">Montant</th><th>Réf.</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.operationType || '—'}</td>
                  <td>{r.staffName || '—'}</td>
                  <td>{r.category || '—'}</td>
                  <td className="mono">{r.receivedAt ? new Date(r.receivedAt).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>{PAYMENT_METHODS[r.method] || r.method || '—'}</td>
                  <td className="num"><b>{formatXof(r.amountXof)}</b></td>
                  <td className="mono">{r.receiptNumber || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
      {modal && <DepenseModal staff={staff} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function DepenseModal({ staff, onClose, onSaved }) {
  const [f, setF] = useState({ operationType: 'Paie', staffId: '', category: 'Salaire', amountXof: '', method: 'VIREMENT', reference: '', payrollPeriod: '', notes: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.amountXof) { setError('Le montant est obligatoire.'); return; }
    setSaving(true);
    try {
      await api.post('/finance/payments', {
        amountXof: Number(f.amountXof),
        method: f.method,
        reference: f.reference || undefined,
        notes: f.notes || undefined,
        staffId: f.staffId || undefined,
        operationType: f.operationType,
        category: f.category,
        payrollPeriod: f.payrollPeriod || undefined,
      });
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Nouvelle dépense / paie</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="field"><label>Type d'opération</label>
          <select value={f.operationType} onChange={set('operationType')}>
            {OPERATION_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field"><label>Agent concerné</label>
          <select value={f.staffId} onChange={set('staffId')}>
            <option value="">— Général —</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
          </select>
        </div>
        <div className="field"><label>Catégorie</label>
          <select value={f.category} onChange={set('category')}>
            <option value="Salaire">Salaire</option>
            <option value="Dépense fonctionnement">Dépense fonctionnement</option>
            <option value="Avance">Avance sur salaire</option>
            <option value="Autre">Autre</option>
          </select>
        </div>
        {['Paie', 'Bulletin de paie', 'Avance sur salaire'].includes(f.operationType) && (
          <div className="field"><label>Période de paie</label>
            <input value={f.payrollPeriod} onChange={set('payrollPeriod')} placeholder="Ex : Août 2026" />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field"><label>Montant (XOF) *</label>
            <input type="number" min="1" value={f.amountXof} onChange={set('amountXof')} />
          </div>
          <div className="field"><label>Moyen de paiement</label>
            <select value={f.method} onChange={set('method')}>
              {Object.entries(PAYMENT_METHODS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Référence</label>
          <input value={f.reference} onChange={set('reference')} placeholder="N° de pièce ou reçu" />
        </div>
        <div className="field"><label>Notes</label>
          <textarea value={f.notes} onChange={set('notes')} rows={2} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
